// components/layout/Layout.tsx
import { ReactNode, useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Sidebar from "./Sidebar";
import { RightSidebar } from "./RightSidebar";

interface LayoutProps {
  children?: ReactNode;
  type?: "app" | "auth" | "landing";
}

export function Layout({ children, type = "app" }: LayoutProps) {
  // Only show sidebar in app layout
  const showSidebar = type === "app";
  const navigate = useNavigate();
  const [showRightSidebar, setShowRightSidebar] = useState(true);

  // Load right sidebar visibility from localStorage
  useEffect(() => {
    const storedState = localStorage.getItem('rightSidebar_visible');
    if (storedState !== null) {
      setShowRightSidebar(storedState === 'true');
    }
  }, []);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate(`/links?search=${encodeURIComponent(query.trim())}`);
    }
  };

  let mainClassName = "flex-1 overflow-hidden";
  if (type === "app") {
    // For app, sidebars are present, so apply margin and max-width
    // Adjust width based on right sidebar visibility
    mainClassName += ` px-6 py-6 ml-12 ${showRightSidebar ? 'max-w-[calc(100%-24rem)]' : 'max-w-[calc(100%-4rem)]'} min-w-0`;
  } else if (type === "auth") {
    // For auth pages, usually a centered form, so padding is good.
    mainClassName += ` px-6 py-6`;
  }
  // For type === "landing", mainClassName remains "flex-1".
  // Landing page sections (Hero, Features, etc.) will manage their own full-width backgrounds and internal containers.

  return (
    <div className="flex min-h-screen flex-col">
      <Header variant={type === "landing" ? "transparent" : "default"} onSearch={type === "app" ? handleSearch : undefined} />
      <div className="flex flex-1 min-h-0">
        {showSidebar && (
          <div className="sidebar-container sticky top-[4rem] h-[calc(100vh-4rem)] flex-shrink-0">
            <Sidebar />
          </div>
        )}
        <main className={mainClassName}>
          <div className="h-full overflow-auto">
            {children || <Outlet />}
          </div>
        </main>
        {showSidebar && <RightSidebar />}
      </div>
      <Footer />
    </div>
  );
}

export default Layout;